import { useId, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { Question } from '../../config/types';
import { objectToString, stringToObject, validateField } from '../../utils/form-utils';
import Details from './Details';
import ErrorMessage from './ErrorMessage';

type Props = {
  question: Question;
  disabled?: boolean;
  anchorId?: string;
  detailsHeading?: string;
  detailsContent?: string;
};

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const parseReportMonth = (value: string) => {
  const parsed = stringToObject(value);
  return {
    month: typeof parsed?.month === 'string' ? parsed.month : '',
    year: typeof parsed?.year === 'string' ? parsed.year : '',
  };
};

export function ReportMonth({
  question,
  disabled,
  anchorId,
  detailsHeading,
  detailsContent,
}: Props) {
  const { register, getFieldState, getValues, setValue } = useFormContext();
  const { error } = getFieldState(question.name);
  const id = useId();
  const baseId = anchorId ?? id;
  const isReadOnly = Boolean(disabled || question.readOnly);

  const initialValue = useMemo(
    () => String(getValues(question.name) ?? ''),
    [getValues, question.name],
  );
  const initialParsed = useMemo(() => parseReportMonth(initialValue), [initialValue]);

  const [month, setMonth] = useState(initialParsed.month);
  const [year, setYear] = useState(initialParsed.year);

  const updateValue = (nextMonth: string, nextYear: string) => {
    const combined =
      nextMonth || nextYear ? objectToString({ month: nextMonth, year: nextYear }) : '';
    setValue(question.name, combined, {
      shouldDirty: true,
      shouldValidate: false,
    });
  };

  const { ref } = register(question.name, {
    validate: (value: string) => {
      const parsed = parseReportMonth(value);
      const combinedValue = parsed.month && parsed.year ? `${parsed.month} ${parsed.year}` : '';

      if (question.rules?.required) {
        const requiredError = validateField(
          question.title,
          { required: true },
          combinedValue,
          question.errorMessages,
        );
        if (requiredError) return requiredError;
      } else if (!combinedValue) {
        return undefined;
      }

      if (!/^\d{4}$/.test(parsed.year)) {
        return 'Reporting Period year must be 4 digits';
      }

      return undefined;
    },
  });

  return (
    <>
      {error && <ErrorMessage error={error} />}
      {detailsHeading && <Details heading={detailsHeading} content={detailsContent} />}

      <input type="hidden" ref={ref} name={question.name} />

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div>
          <label className="govuk-label" htmlFor={`${baseId}-month`}>
            Month
          </label>
          <select
            id={`${baseId}-month`}
            className="govuk-select"
            disabled={isReadOnly}
            value={month}
            onChange={(event) => {
              const nextMonth = event.target.value;
              setMonth(nextMonth);
              updateValue(nextMonth, year);
            }}
          >
            <option value="">Select month</option>
            {MONTHS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="govuk-label" htmlFor={`${baseId}-year`}>
            Year
          </label>
          <input
            id={`${baseId}-year`}
            className="govuk-input govuk-input--width-4"
            disabled={isReadOnly}
            inputMode="numeric"
            maxLength={4}
            pattern="[0-9]{4}"
            value={year}
            onChange={(event) => {
              const nextYear = event.target.value.replace(/\D/g, '').slice(0, 4);
              setYear(nextYear);
              updateValue(month, nextYear);
            }}
          />
        </div>
      </div>
    </>
  );
}
